import _ from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { checkKeyPress } from "../../scripts/helperScripts";
import "../../styles/components/Inbox.css";
import Input from "../common/Input";
import { saveMessage } from "../services/soar";

const DirectMessages = ({ directMessages, unreadClients }) => {
  const portalState = JSON.parse(localStorage.getItem("safyInfo") || "{}");

  const portalUser = useMemo(() => {
    return portalState?.user || {};
  }, [portalState]);

  const safyInfo = useMemo(() => {
    return portalUser?.safyInfo;
  }, [portalUser]);

  const userId = useMemo(() => {
    return safyInfo?.SOARUserId;
  }, [safyInfo]);

  const userName = useMemo(() => {
    if (!portalUser?.surname || !portalUser.givenName) {
      return;
    }

    return `${portalUser.surname}, ${portalUser.givenName}`;
  }, [portalUser]);

  const [messageData, setMessageData] = useState({
    DiscussionTitle: "",
    InteractionType: "",
    Priority: "",
    CreatedById: userId,
    CreatedByName: userName,
    DiscussionBody: "",
  });
  const [messageHistory, setMessageHistory] = useState([]);
  const [selectedClient, setSelectedClient] = useState();
  const [selectedFosterParent, setSelectedFosterParent] = useState();

  const groupedByClient = useMemo(() => {
    const groupedObj = _.groupBy(directMessages, (m) => m.ClientId);
    return groupedObj;
  }, [directMessages]);

  const groupedMessages = useMemo(() => {
    const groupedObj = _.groupBy(
      groupedByClient[selectedClient],
      (m) => m.FosterParentId
    );
    return groupedObj;
  }, [groupedByClient, selectedClient]);

  const sortedMessages = useMemo(() => {
    const groupedObj = _.sortBy(groupedMessages, (m) =>
      moment(m[0].CreatedOn)
    ).reverse();
    return groupedObj;
  }, [groupedMessages]);

  const handleOpenClient = useCallback(
    (clientId) => {
      if (selectedClient === clientId) {
        setSelectedClient(undefined);
      } else {
        setSelectedClient(clientId);
      }

      setSelectedFosterParent(undefined);
    },
    [selectedClient]
  );

  const handleOpenDiscussion = useCallback(
    (fosterParentId) => {
      if (selectedFosterParent === fosterParentId) {
        setSelectedFosterParent(undefined);
        setMessageHistory([]);
        setMessageData({
          DiscussionTitle: "",
          InteractionType: "",
          Priority: "",
          CreatedById: userId,
          CreatedByName: userName,
          DiscussionBody: "",
        });
      } else {
        setSelectedFosterParent(fosterParentId);
        const history = [...groupedMessages[fosterParentId]];
        const message = history[0];

        // mark as read
        if (
          message.DiscussionItemStatus === "Sent" &&
          message.CreatedById === message.FosterParentId
        ) {
          message.DiscussionItemStatus = "Read";

          const statusChangeObj = {
            DiscussionId: message.DiscussionId,
            DiscussionItemStatus: "Read",
          };

          saveMessage(statusChangeObj, "StatusChange");
        }

        setMessageHistory(history.reverse());
        setMessageData({
          ...message,
          CreatedById: userId,
          CreatedByName: userName,
          DiscussionBody: "",
        });
      }
    },
    [selectedFosterParent, groupedMessages, userId, userName]
  );

  useEffect(() => {
    if (messageHistory?.length > 0) {
      const discussionElement =
        document.getElementsByClassName("discussion-thread")[0];
      discussionElement.scrollTop = discussionElement.scrollHeight;
    }
  }, [messageHistory]);

  const appendNewMessage = (m) => {
    setMessageHistory((mh) => {
      const obj = [...mh];
      obj.push(m);
      return obj;
    });
  };

  const renderMessages = useCallback(() => {
    const getClassName = (message) => {
      if (message.CreatedById === userId) {
        return "ms-5 ms-sm-4 p-2 float-end sent-discussion";
      } else {
        return "me-5 me-sm-4 p-2 float-start received-discussion";
      }
    };

    return (
      <>
        {messageHistory.map((message) => (
          <div
            key={message.DiscussionId}
            className="row mt-2 position-relative"
          >
            <div className="col">
              <div className={getClassName(message)}>
                <div className="fw-bold">
                  {moment(message.CreatedOn).format("l LTS")}
                </div>
                <div className="border border-1 p-2 rounded message">
                  {message.DiscussionBody}
                </div>
              </div>
            </div>
          </div>
        ))}
      </>
    );
  }, [userId, messageHistory]);

  const handleChange = ({ currentTarget: input }) => {
    setMessageData((md) => ({ ...md, [input.name]: input.value }));
  };

  const handleSend = () => {
    const message = {
      ReplyToId: messageData.DiscussionId,
      ClientId: messageData.ClientId,
      ClientName: messageData.ClientName,
      TeamMemberId: userId,
      TeamMemberName: userName,
      FosterParentId: messageData.FosterParentId,
      FosterParentName: messageData.FosterParentName,
      DiscussionTitle: messageData.DiscussionTitle,
      DiscussionBody: messageData.DiscussionBody,
      DiscussionStatus: !messageData.ReplyToId ? "New" : "Reply",
      DiscussionItemStatus: "Sent",
      CreatedById: userId,
      CreatedByName: userName,
      InteractionType: messageData.InteractionType || null,
      Priority: messageData.Priority || null,
      RecordedServiceId: messageData.RecordedServiceId || null,
    };

    const send = async () => {
      const sendingToastId = toast.loading("Sending...", {
        autoClose: 5000,
        closeOnClick: true,
        isLoading: false,
        pauseOnFocusLoss: false,
      });
      const response = await saveMessage(message, "Create");
      if (response?.code !== "fail") {
        message.DiscussionId = response.id;
        appendNewMessage(message);
        toast.update(sendingToastId, {
          render: "Sent!",
          autoClose: 5000,
          closeOnClick: true,
          isLoading: false,
          pauseOnFocusLoss: false,
          type: "success",
        });
      } else {
        toast.update(sendingToastId, {
          render: "Failed to send...",
          autoClose: 5000,
          closeOnClick: true,
          isLoading: false,
          pauseOnFocusLoss: false,
          type: "error",
        });
      }
    };

    send();

    const statusChangeObj = {
      DiscussionId: message.ReplyToId,
      DiscussionItemStatus: "Replied",
    };

    saveMessage(statusChangeObj, "StatusChange");
  };

  const ClientHint = useCallback(
    (message, selected) => {
      const MessageHint = (msg, isOpen) => (
        <div
          key={msg.DiscussionId}
          className={`ms-3 ms-sm-2 ms-lg-3 p-2 discussion-hint ${
            isOpen ? "bg-safy-primary2" : "bg-safy-secondary"
          }${
            msg.CreatedById === msg.FosterParentId &&
            msg.DiscussionItemStatus === "Sent"
              ? " unread"
              : ""
          }`}
          onClick={() => {
            handleOpenDiscussion(msg.FosterParentId);
          }}
        >
          {msg.FosterParentName} - {moment(msg.CreatedOn).format("l")}
          <br />
          {msg.CreatedById === userId ? "You: " : ""}
          {msg.DiscussionBody.length > 50
            ? `${msg.DiscussionBody.substring(0, 50)}...`
            : msg.DiscussionBody}
        </div>
      );

      return (
        <React.Fragment key={message.DiscussionId}>
          <div
            className={`p-2 discussion-hint ${
              selected ? "bg-safy-primary" : "bg-safy-secondary"
            }${unreadClients.indexOf(message.ClientId) > -1 ? " unread" : ""}`}
            onClick={() => {
              handleOpenClient(message.ClientId);
            }}
          >
            <div className="col h6">
              {message.ClientName} - {moment(message.CreatedOn).format("l")}
            </div>
          </div>
          {selected
            ? Object.keys(sortedMessages).map((key) =>
                MessageHint(
                  sortedMessages[key][0],
                  sortedMessages[key][0].FosterParentId === selectedFosterParent
                )
              )
            : null}
        </React.Fragment>
      );
    },
    [
      unreadClients,
      sortedMessages,
      handleOpenClient,
      handleOpenDiscussion,
      userId,
      selectedFosterParent,
    ]
  );

  const [viewportWidth, setViewportWidth] = useState(
    Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
  );

  useEffect(() => {
    const getViewportWidth = () => {
      setViewportWidth(
        Math.max(
          document.documentElement.clientWidth || 0,
          window.innerWidth || 0
        )
      );
    };

    window.addEventListener("resize", getViewportWidth, true);
    return () => window.removeEventListener("resize", getViewportWidth);
  }, []);

  const handleKeyDown = (e) => {
    if (checkKeyPress(e)) {
      handleSend();
    }
  };

  return viewportWidth < 576 ? (
    <div className="inbox-container container-fluid">
      <div className="row">
        {!selectedFosterParent ? (
          <div className="hint-container col">
            {Object.keys(groupedByClient).map((key) =>
              ClientHint(groupedByClient[key][0], key === selectedClient)
            )}
          </div>
        ) : (
          <div className="discussion-container col">
            <div className="discussion-header">
              <div className="d-flex p-2">
                <div className="me-2">
                  <i
                    className="fas fa-xmark fa-2x text-danger"
                    onClick={() => {
                      handleOpenDiscussion(selectedFosterParent);
                    }}
                  />
                </div>
                <div className="flex-fill me-2 fw-bold">
                  {messageHistory[0].FosterParentName}
                  <br />
                  RE: {messageHistory[0].ClientName}
                </div>
              </div>
            </div>
            <div className="discussion-thread">{renderMessages()}</div>
            <div className="new-discussion px-2">
              <div className="d-flex p-2">
                <div className="flex-fill me-2">
                  <Input
                    name="DiscussionBody"
                    value={messageData.DiscussionBody}
                    onChange={handleChange}
                    placeholder="Type a message"
                    onKeyDown={handleKeyDown}
                    divClassName="mb-0"
                  />
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleSend();
                  }}
                >
                  <i className="far fa-paper-plane" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  ) : (
    <div className="inbox-container row g-sm-0">
      <div className="hint-container col-6 col-md-5 col-lg-4 col-xxl-3">
        {Object.keys(groupedByClient).map((key) =>
          ClientHint(groupedByClient[key][0], key === selectedClient)
        )}
      </div>
      <div className="discussion-container col">
        <div className="discussion-thread px-2">
          {selectedFosterParent ? renderMessages() : null}
        </div>
        <div className="new-discussion px-2">
          <div className="d-flex p-2">
            <div className="flex-fill me-2">
              <Input
                name="DiscussionBody"
                value={messageData.DiscussionBody}
                onChange={handleChange}
                placeholder="Type a message"
                onKeyDown={handleKeyDown}
                divClassName="mb-0"
              />
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={() => {
                  handleSend();
                }}
              >
                <i className="far fa-paper-plane" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectMessages;
